import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star, ImagePlus } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import MediaPicker from "../../components/MediaPicker";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 bg-white";

const EMPTY = {
  title: "", images: [], destination: "", description: "", price_label: "Starting at",
  price: "", travel_dates: "", booking_deadline: "", terms: "",
  button_text: "View Offer / Book", button_url: "https://www.evotravelagent.com/ldixontravel",
  featured: false, order: 0, status: "draft", expires_at: "",
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

export default function AdminPromotions() {
  const [promos, setPromos] = useState(null);
  const [editing, setEditing] = useState(null); // null | promo object
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promotions").then(({ data }) => setPromos(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Please give the promotion a title"); return; }
    setSaving(true);
    try {
      const payload = { ...editing, order: Number(editing.order) || 0, expires_at: editing.expires_at || "" };
      if (editing.id) {
        await api.put(`/admin/promotions/${editing.id}`, payload);
      } else {
        await api.post("/admin/promotions", payload);
      }
      toast.success(editing.status === "published" ? "Promotion published" : "Promotion saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e, "Could not save promotion"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" permanently? Consider archiving it instead to keep a record.`)) return;
    await api.delete(`/admin/promotions/${p.id}`);
    toast.success("Promotion deleted");
    load();
  };

  const statusBadge = (s) => {
    const map = { published: "bg-emerald-100 text-emerald-800", draft: "bg-amber-100 text-amber-800", archived: "bg-slate-200 text-slate-600" };
    return <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${map[s] || map.draft}`}>{s}</span>;
  };

  if (editing) {
    const set = (patch) => setEditing({ ...editing, ...patch });
    return (
      <div data-testid="admin-promotion-editor">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">{editing.id ? "Edit Promotion" : "New Promotion"}</h2>
          <button onClick={() => setEditing(null)} data-testid="promo-editor-cancel" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-600">
            <X className="w-4 h-4" /> Cancel
          </button>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 grid gap-5 sm:grid-cols-2">
          <Field label="Promotion title *"><input value={editing.title} onChange={(e) => set({ title: e.target.value })} className={inputCls} data-testid="promo-field-title" /></Field>
          <Field label="Destination / cruise info"><input value={editing.destination} onChange={(e) => set({ destination: e.target.value })} className={inputCls} placeholder="e.g. 7-Night Caribbean Cruise" /></Field>
          <div className="sm:col-span-2">
            <Field label="Description"><textarea rows={4} value={editing.description} onChange={(e) => set({ description: e.target.value })} className={inputCls} /></Field>
          </div>
          <div className="sm:col-span-2">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Photos</span>
            <div className="flex flex-wrap gap-3">
              {(editing.images || []).map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img.url} alt={img.alt || ""} className="w-28 h-20 object-cover rounded-lg border border-slate-200" />
                  <button onClick={() => set({ images: editing.images.filter((_, idx) => idx !== i) })} aria-label="Remove photo"
                    className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button onClick={() => setPicker(true)} data-testid="promo-add-photo" className="w-28 h-20 rounded-lg border-2 border-dashed border-amber-400/60 flex flex-col items-center justify-center text-amber-700 text-xs font-semibold hover:bg-amber-50">
                <ImagePlus className="w-4 h-4" /> Add photo
              </button>
            </div>
            <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(sel) => set({ images: [...(editing.images || []), sel] })} />
          </div>
          <Field label="Price label"><input value={editing.price_label} onChange={(e) => set({ price_label: e.target.value })} className={inputCls} placeholder="Starting at" /></Field>
          <Field label="Price (optional)"><input value={editing.price} onChange={(e) => set({ price: e.target.value })} className={inputCls} placeholder="e.g. $899 per person" /></Field>
          <Field label="Travel dates (optional)"><input value={editing.travel_dates} onChange={(e) => set({ travel_dates: e.target.value })} className={inputCls} placeholder="e.g. June–August 2026" /></Field>
          <Field label="Booking deadline (optional)"><input value={editing.booking_deadline} onChange={(e) => set({ booking_deadline: e.target.value })} className={inputCls} placeholder="e.g. March 31, 2026" /></Field>
          <div className="sm:col-span-2">
            <Field label="Terms & restrictions (optional)"><textarea rows={3} value={editing.terms} onChange={(e) => set({ terms: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Button text"><input value={editing.button_text} onChange={(e) => set({ button_text: e.target.value })} className={inputCls} /></Field>
          <Field label="Button link" hint="Default sends visitors to your travel website. Paste a specific offer link to override.">
            <input value={editing.button_url} onChange={(e) => set({ button_url: e.target.value })} className={inputCls} data-testid="promo-field-url" />
          </Field>
          <Field label="Stop showing publicly after (optional)">
            <input type="datetime-local" value={editing.expires_at ? editing.expires_at.slice(0, 16) : ""} onChange={(e) => set({ expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className={inputCls} data-testid="promo-field-expiry" />
          </Field>
          <Field label="Display order (lower shows first)"><input type="number" value={editing.order} onChange={(e) => set({ order: e.target.value })} className={inputCls} /></Field>
          <div className="flex items-center gap-6 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={editing.featured} onChange={(e) => set({ featured: e.target.checked })} className="accent-amber-600 w-4 h-4" data-testid="promo-field-featured" />
              <Star className="w-4 h-4 text-amber-500" /> Featured (shows on Home)
            </label>
            <Field label="Status">
              <select value={editing.status} onChange={(e) => set({ status: e.target.value })} className={inputCls} data-testid="promo-field-status">
                <option value="draft">Draft (hidden)</option>
                <option value="published">Published (visible)</option>
                <option value="archived">Archived (hidden)</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} data-testid="promo-editor-save" className="btn-brand !rounded-lg disabled:opacity-60">
            {saving ? "Saving…" : editing.status === "published" ? "Save & Publish" : "Save Promotion"}
          </button>
          <button onClick={() => setEditing(null)} className="btn-outline !rounded-lg !text-[#0B1D3A]">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Promotions</h2>
          <p className="text-slate-500 text-sm">Create and manage the offers shown on your website.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="admin-promo-create-button" className="btn-brand !rounded-lg !py-2.5">
          <Plus className="w-4 h-4" /> New Promotion
        </button>
      </div>

      {promos === null ? (
        <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>
      ) : promos.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <p className="text-slate-500">No promotions yet. Create your first one — or leave this empty and your website will show a friendly "coming soon" message.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-promo-row-${p.id}`}>
              {p.images?.[0] ? (
                <img src={p.images[0].url} alt="" className="w-20 h-14 object-cover rounded-lg border border-slate-200 shrink-0" />
              ) : (
                <div className="w-20 h-14 rounded-lg bg-slate-100 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0B1D3A] truncate flex items-center gap-2">
                  {p.title} {p.featured && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" aria-label="Featured" />}
                </p>
                <p className="text-xs text-slate-500 truncate">{p.destination || "No destination"} {p.price ? `· ${p.price_label} ${p.price}` : ""}</p>
              </div>
              {statusBadge(p.status)}
              <button onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`admin-promo-edit-${p.id}`} aria-label={`Edit ${p.title}`} className="p-2 rounded-lg hover:bg-slate-100"><Pencil className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => remove(p)} data-testid={`admin-promo-delete-${p.id}`} aria-label={`Delete ${p.title}`} className="p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
